import { useState, useEffect } from "react";
import styles from "./TablePagination.module.scss"

interface TablePaginationProps {
  totalItems: number
  itemsPerPage: number
  currentPage: number
  onPageChange: (page: number) => void
  className?: string;
  isLoading?: boolean;
}

export function TablePagination({
  totalItems,
  itemsPerPage,
  currentPage,
  onPageChange,
  className,
  isLoading
}: Readonly<TablePaginationProps>) {
  const [pageNumbers, setPageNumbers] = useState<(number | string)[]>([])
  const totalPages = Math.ceil(totalItems / itemsPerPage)

  const startItem = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  // Hide pagination only when there are no items or still loading
  const shouldHidePagination = isLoading || totalItems === 0;
  // Disable all controls when there's only one page
  const isSinglePage = totalPages <= 1;

  useEffect(() => {
    const generatePageNumbers = () => {
      // Show all pages if 9 or fewer
      if (totalPages <= 9) {
        return Array.from({ length: totalPages }, (_, i) => i + 1)
      }

      const pages: (number | string)[] = []

      // Always show first two pages
      pages.push(1, 2)

      // Determine if we need left ellipsis (current page is far from the beginning)
      const needsLeftEllipsis = currentPage > 5

      // Determine if we need right ellipsis (current page is far from the end)
      const needsRightEllipsis = currentPage < totalPages - 4

      if (needsLeftEllipsis) {
        pages.push('...')
      }

      // Calculate middle pages around current page
      let startMiddle: number
      let endMiddle: number

      if (currentPage <= 5) {
        // Near the beginning: show pages 3, 4, 5
        startMiddle = 3
        endMiddle = 5
      } else if (currentPage >= totalPages - 4) {
        // Near the end: show pages (last-4), (last-3), (last-2)
        startMiddle = totalPages - 4
        endMiddle = totalPages - 2
      } else {
        // In the middle: show current-1, current, current+1
        startMiddle = currentPage - 1
        endMiddle = currentPage + 1
      }

      // Add middle pages
      for (let i = startMiddle; i <= endMiddle; i++) {
        pages.push(i)
      }

      if (needsRightEllipsis) {
        pages.push('...')
      }

      // Always show last two pages
      pages.push(totalPages - 1, totalPages)

      return pages
    }

    setPageNumbers(generatePageNumbers())
  }, [totalItems, itemsPerPage, currentPage, totalPages])

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page)
    }
  }

  // Hide pagination only when there are no items or still loading
  if (shouldHidePagination) {
    return null;
  }

  return (
    <div className={`${styles.paginationContainer} ${className ?? ''}`}>
      <div className={styles.paginationInfo}>
        Showing {startItem} to {endItem} of {totalItems} entries
      </div>

      <div className={styles.paginationControls}>
        <button
          className={styles.paginationButton}
          onClick={() => goToPage(1)}
          disabled={isSinglePage || currentPage === 1}
        >
          First
        </button>

        <button
          className={styles.paginationButton}
          onClick={() => goToPage(currentPage - 1)}
          disabled={isSinglePage || currentPage === 1}
        >
          Prev
        </button>

        {pageNumbers.map((page, index) => (
          page === '...' ? (
            <span key={`ellipsis-${index}`} className={styles.ellipsis}>
              ...
            </span>
          ) : (
            <button
              key={page}
              className={`${styles.paginationButton} ${currentPage === page ? styles.paginationButtonActive : ''}`}
              onClick={() => goToPage(page as number)}
              disabled={isSinglePage}
            >
              {page}
            </button>
          )
        ))}

        <button
          className={styles.paginationButton}
          onClick={() => goToPage(currentPage + 1)}
          disabled={isSinglePage || currentPage === totalPages}
        >
          Next
        </button>

        <button
          className={styles.paginationButton}
          onClick={() => goToPage(totalPages)}
          disabled={isSinglePage || currentPage === totalPages}
        >
          Last
        </button>
      </div>
    </div>
  )
}